import type { IntakeItem } from "@/features/intake/types/intake";

/**
 * Which kind of value a Basic Information item holds, matched from its key and
 * label (both are editable in the source Sheet, so matching stays loose).
 *
 * Kept apart from `intakeFieldKind` deliberately: that module also exports the
 * phone country-code list, which pulls in a country dataset. These predicates are
 * pure string matching, and importing them must not drag that into a bundle.
 */
function normalizeToken(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export type CredentialField = {
  provider: "caqh" | "cms";
  part: "username" | "password";
};

/**
 * Identifies the credential rows that are presented together in Basic Information.
 * The source Sheet is editable, so match both its stable-ish key and visible label.
 */
export function getCredentialField(item: IntakeItem): CredentialField | null {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  const provider =
    key.includes("caqh") || label.includes("caqh")
      ? "caqh"
      : key.includes("cms") || label.includes("cms")
        ? "cms"
        : null;

  if (!provider) return null;
  if (key.includes("username") || label.includes("username")) {
    return { provider, part: "username" };
  }
  if (key.includes("password") || label.includes("password")) {
    return { provider, part: "password" };
  }
  return null;
}

export type BasicInfoLayoutItem =
  | { kind: "field"; item: IntakeItem }
  | { kind: "credentials"; provider: "caqh" | "cms"; items: [IntakeItem, IntakeItem] };

/** Pair known login rows without changing the Sheet-backed values or save payload. */
export function groupBasicInfoItems(items: IntakeItem[]): BasicInfoLayoutItem[] {
  const credentials = new Map<
    "caqh" | "cms",
    Partial<Record<"username" | "password", IntakeItem>>
  >();

  for (const item of items) {
    const match = getCredentialField(item);
    if (!match) continue;
    const pair = credentials.get(match.provider) ?? {};
    // If the Sheet accidentally contains a duplicate, leave the extra row alone
    // rather than silently hiding it inside the group.
    if (!pair[match.part]) pair[match.part] = item;
    credentials.set(match.provider, pair);
  }

  const groupedKeys = new Set<string>();
  const result: BasicInfoLayoutItem[] = [];
  for (const item of items) {
    if (groupedKeys.has(item.key)) continue;
    const match = getCredentialField(item);
    const pair = match ? credentials.get(match.provider) : undefined;
    const itemBelongsToPair = pair?.username?.key === item.key || pair?.password?.key === item.key;
    if (match && pair?.username && pair.password && itemBelongsToPair) {
      result.push({
        kind: "credentials",
        provider: match.provider,
        items: [pair.username, pair.password],
      });
      groupedKeys.add(pair.username.key);
      groupedKeys.add(pair.password.key);
    } else {
      result.push({ kind: "field", item });
      groupedKeys.add(item.key);
    }
  }
  return result;
}

export function isAddressField(item: IntakeItem): boolean {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  return key === "address" || label.includes("address");
}

export function isFullLegalNameField(item: IntakeItem): boolean {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  return (
    key === "fullname" ||
    label === "fullname" ||
    key.includes("fulllegalname") ||
    label.includes("fulllegalname") ||
    key.includes("legalname") ||
    label.includes("legalname")
  );
}

export function isPhoneField(item: IntakeItem): boolean {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  return key === "phone" || label.includes("phone");
}

export function isPlaceOfBirthField(item: IntakeItem): boolean {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  return (
    key.includes("placeofbirth") ||
    key.includes("birthplace") ||
    label.includes("placeofbirth") ||
    label.includes("birthplace")
  );
}

/**
 * Matched ahead of {@link isPlaceOfBirthField}; the two are distinct fields and
 * neither substring appears in the other ("dateofbirth" vs "placeofbirth").
 */
export function isDateOfBirthField(item: IntakeItem): boolean {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  return (
    key === "dob" ||
    label === "dob" ||
    key.includes("dateofbirth") ||
    key.includes("birthdate") ||
    label.includes("dateofbirth") ||
    label.includes("birthdate")
  );
}

export function isEmailField(item: IntakeItem): boolean {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  return key.includes("email") || label.includes("email");
}

/**
 * "npi" on a word boundary in the label's original text.
 *
 * Unlike its siblings this doesn't substring-match the normalized label: "npi"
 * is only three letters, and normalizing strips the separators that would bound
 * it — "Green Pines Hospital" becomes "greenpineshospital", which contains
 * "npi". A false positive is worse than a miss here, since it forces a
 * digits-only, 10-character input onto a field that isn't an NPI.
 */
const NPI_LABEL_RE = /\bnpi\b/i;

export function isNpiField(item: IntakeItem): boolean {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  return (
    // Keys are stable slugs set in the sheet ("npi", "npi_number"), so a loose
    // match is safe on them.
    key.includes("npi") ||
    NPI_LABEL_RE.test(item.label) ||
    // Still catches a label whose separators carried the boundary, e.g. "N.P.I."
    label === "npi" ||
    label.includes("nationalprovideridentifier")
  );
}

export function isPasswordField(item: IntakeItem): boolean {
  const key = normalizeToken(item.key);
  const label = normalizeToken(item.label);
  return key.includes("password") || label.includes("password");
}

/** Digits-only input, capped at the NPI's fixed 10-digit length. */
export function formatNpiInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 10);
}

/** Digits-only phone number input. The country calling code is selected separately. */
export function formatPhoneDigits(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 15);
}
